import { useContext } from 'react';
import { Context } from '@front10/helpers/dist/Context';

type TranslateFn = (key: string, defaultValue?: string) => string;

export function strFormat(format: string, params: Record<string, any>) {
  const regex = new RegExp(
    Object.keys(params)
      .map((el) => `\\{${el}\\}`)
      .join('|'),
    'g'
  );

  return format.replace(regex, (match) => params[match.substr(1, match.length - 2)]);
}

const withParams = (translate: TranslateFn) => (
  key: string,
  defaultValue?: string,
  params?: object
) => {
  const translation = translate(key, defaultValue);

  if (params === undefined || params === null || typeof params !== 'object') {
    return translation;
  }

  return strFormat(translation, params);
};

interface PluralArgs {
  count: number;
  key: string;
  variables?: object;
  defaultValue: string;
}

const pluralize = (translate: TranslateFn) => {
  const translateWithParams = withParams(translate);
  return ({ count, key, variables = {}, defaultValue }: PluralArgs) => {
    const args = { count, ...variables };
    const pluralKey = `${key}_${count}`;

    const plural = translateWithParams(pluralKey, undefined, args);
    if (plural !== pluralKey) {
      return plural;
    }
    return translateWithParams(key, defaultValue, args);
  };
};

/**
 * This hook returns an enhanced translation function that handles
 * plural translations using the `translate(key, defaultValue)` function provided by
 * Context. It will try to match a translation using a dynamic key of the
 * form `${key}_${count}`. If it doesn't exist it will lookup for
 * translate(key). It will make interpolations for all elements inside
 * `variables` and `count` (See `useTranslation`).
 *
 * Example:
 *
 * Given the keys:
 * translate_apple: 'There are {count} apples.'
 * translate_apple_0: 'There are no apples.'
 * translate_apple_1: 'There is one apple.'
 *
 * const translate = usePlural();
 * translate({ count: 0, key: 'translate_apple' }); // There are no apples.
 * translate({ count: 1, key: 'translate_apple' }); // There is one apple.
 * translate({ count: 5, key: 'translate_apple' }); // There are 5 apples.
 */
export const usePlural = () => {
  const { translate } = useContext(Context);
  return pluralize(translate);
};

/**
 * This hook returns an enhanced translation function that handles
 * interpolations using the `translate(key, defaultValue)` function provided by
 * Context. The new function has the form translate(key, defaultValue, args).
 *
 * Example:
 *
 * ```js
 * const translate = useTranslation();
 * translate(
 *    'translate-key',
 *    'The quick brown {animal} {action} over the lazy dog',
 *    { animal: 'fox', action: 'jumps' }
 * );
 * ```
 *
 * Will give:
 * The quick brown fox jumps over the lazy dog
 */
export const useTranslation = () => {
  const { translate } = useContext(Context);
  return withParams(translate);
};

export const useDirection = () => {
  const { direction } = useContext(Context);
  return direction;
};
